import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import {
  Button,
  SubTable,
  Textarea,
  type SubTableColumn,
} from "@/design-system/components/ui";
// Modal is a staff-shell component, not part of the shared portal kit.
import { Modal } from "@/components/ui/Modal";
import type { SenderIdentity } from "./useSenderIdentity";

/**
 * The one screen a buyer-module email passes through before it goes.
 *
 * <p>The module has FIVE specialist-composed outbound emails — the listing intro
 * email to the BUYER, the submission package to the LISTING BROKER, the same
 * package to the BUYER, and the prequalification invitation to the BUYER and to
 * the SELLER — and four of them are confirmed here. Each had grown its own
 * confirmation with a different amount of the message on it: the package's showed
 * a subject and a body; the intro email's showed a subject, a body and the
 * recipient; the seller invitation had none at all and sent on the first
 * click.</p>
 *
 * <p>⚠️ This doc said "three" while the backend's said "three" and named a
 * DIFFERENT third, so the module already had four when both claimed three. If you
 * are editing this list, edit `BuyersController`'s too — and state the population
 * ("specialist-composed"), because the whole-module send count is larger again
 * once automated transactional mail is included.</p>
 *
 * <p>🔴 <b>Preview and confirm, never an edit gate.</b> Nothing here forces a
 * keystroke and nothing sends on first click. The enforced condition upstream is
 * "a human has had this in front of them", not "a human changed a character" —
 * a draft that came out right should not have to be perturbed to be sendable,
 * and nothing can distinguish a considered acceptance from an ignored one. A
 * client-side "I have edited this" checkbox would contradict that and teach
 * specialists to type a space.</p>
 *
 * <p>🔴 <b>The recipient is stated, never implied.</b> These sends go to three
 * different sides of the same transaction, and the Submission package tab now
 * composes TWO of them — the same document to the listing broker and to the buyer
 * — one click apart. A confirmation reading only "Send?" would be the one place a
 * specialist could act on the wrong audience without a word of warning.</p>
 *
 * <p>🔴 <b>From is described, not quoted.</b> The application does not know the
 * From display name — it comes from the ACS domain configuration, and the
 * option that looks like it sets it is read by nothing — so the identity block
 * says what is true (Phoenix sends it; a reply reaches you) rather than printing
 * a string nothing verified.</p>
 */
export interface SendPreviewAttachment {
  name: string;
  /** Rendered when known. Absent is fine; a wrong size is not. */
  sizeBytes?: number | null;
  /**
   * Fetch the file and hand it to the specialist.
   *
   * <p>🔴 Absent renders the NAME ALONE, which is the right answer whenever the
   * caller cannot promise the link resolves — a dead download is worse than
   * none. Present, it mirrors the picker's own Download column so the two lists
   * offer the same affordance.</p>
   *
   * <p>It exists for the paths Phoenix does not send on: "I sent it myself" and
   * "Copy email body" leave the specialist to attach the file from their own
   * mail client, and naming a file they cannot obtain would be an instruction
   * with no way to follow it.</p>
   */
  onDownload?: () => void;
}

/**
 * One row of the PER-SEND PICKER — the same list, made tickable.
 *
 * <p>🔴 <b>Additive, and the read-only list is still the default.</b> A caller
 * that passes `attachments` renders exactly what it always rendered. Only a
 * caller that passes `selectableAttachments` gets a picker, so the three sends
 * that have nothing to choose between are untouched.</p>
 *
 * <p>🔴 <b>A row that CANNOT be attached is still a row.</b> A document nobody
 * has written yet is the single most useful thing this list can show — omitting
 * it makes an incomplete package look complete, and there is nowhere else on
 * the confirmation that says the document exists at all. It renders untickable
 * with its reason beside it rather than absent.</p>
 */
export interface SendPreviewSelectableAttachment extends SendPreviewAttachment {
  /** Stable row identity. `name` is not it: a missing document is listed by
   *  title and an attached one by file name, and nothing forbids a collision. */
  key: string;
  selected: boolean;
  /** Absent makes the row untickable — the same as `disabled`, and both are
   *  honoured, so a caller cannot produce a tick that does nothing. */
  onToggle?: () => void;
  disabled?: boolean;
  /** Why this row cannot be attached. Rendered IN the row, never as a tooltip:
   *  a `title` attribute is a reason nobody opens. */
  disabledReason?: ReactNode;
  /** The FACT. The sentence is `carriedOverNote`, supplied once by the caller
   *  so this list and the card saying the same thing cannot drift apart. */
  carriedOver?: boolean;
  onDownload?: () => void;
}

export interface SendPreviewModalProps {
  title: string;
  onClose: () => void;

  /** Who it goes to, in words. The name is optional; the address usually is not. */
  toName?: string | null;
  toEmail?: string | null;
  /** One sentence disambiguating the audience — "It does not go to the listing
   *  broker" and its equivalents. */
  audienceNote?: ReactNode;

  /** From `useSenderIdentity`. Omitted while unknown — never guessed. */
  identity?: SenderIdentity;
  /** Set when this send carries NO Reply-To at all, whatever the sender's own
   *  address is — the invitation path, whose mail the prequalification module
   *  composes. Renders the honest "replies reach Phoenix" line instead of the
   *  sender's address. */
  repliesReachPhoenix?: boolean;

  subject?: string | null;
  onSubjectChange?: (value: string) => void;

  body?: string | null;
  onBodyChange?: (value: string) => void;
  bodyRows?: number;

  /**
   * Renders what the recipient will ACTUALLY receive — the branded HTML, from the
   * server, for the subject and body currently on screen.
   *
   * <p>🔴 The preview is fetched, never composed here. Rebuilding the email shell
   * in TypeScript would be a second copy of the design, and a second copy is
   * precisely how this preview came to lie in the first place: the specialist
   * confirmed a left-aligned textarea with its paragraphs intact while the send
   * wrapped that same string in one centred element, and nothing compared them.
   * The server hands back the output of the same composition the send runs.</p>
   *
   * <p>Omitted — as it is for the surfaces that have no such endpoint — the pane
   * simply does not render, and the modal behaves exactly as before.</p>
   */
  renderPreview?: (args: { subject: string; body: string }) => Promise<string>;

  attachments?: readonly SendPreviewAttachment[];

  /**
   * Turns the attachment list into a per-send picker. Wins over `attachments`
   * when both are given — a caller with a picker has already decided.
   */
  selectableAttachments?: readonly SendPreviewSelectableAttachment[];
  /**
   * The provenance sentence for a carried-over row.
   *
   * <p>🔴 Supplied by the caller rather than owned here, because the surface
   * that lists these documents says the same thing on the same documents. Two
   * copies of one sentence is two sentences the moment either is reworded.</p>
   */
  carriedOverNote?: ReactNode;

  /**
   * Text appended to every send by the transport, shown so a specialist can
   * read what actually goes out.
   *
   * <p>🔴 <b>Rendered as TEXT, never as a field.</b> It is a regulated role
   * statement — the one part of the message nobody may edit — and a control
   * that looks editable and silently discards a keystroke is worse than no
   * control at all.</p>
   */
  /**
   * The acting specialist's sign-off, appended to every send by the outreach
   * service and shown here so a specialist reads what actually goes out.
   *
   * <p>🔴 <b>Rendered as TEXT, never as a field</b>, for the reason the role
   * statement below is: it is resolved from the acting user's own profile, so a
   * control that looked editable would silently discard a keystroke — which is
   * worse than no control at all. The place to change it is the profile.</p>
   *
   * <p>OPTIONAL and absent by default, so every existing caller renders exactly
   * as before. A send whose signature is described in a footnote rather than
   * quoted is not wrong; this is for the surfaces that would rather show
   * it.</p>
   */
  signatureBlock?: string | null;

  roleStatement?: string | null;

  /** Actions ON the message — reset, copy. Rendered beneath the fields they
   *  act on. Distinct from the two footer slots, which act on the SEND. */
  messageActions?: ReactNode;

  /**
   * Warnings ABOUT the send, rendered above the subject and alongside it.
   *
   * <p>🔴 This exists because the slot below does NOT. `replacesPreviewBody`
   * REPLACES the message block; a caller with a note to add reaches for the
   * nearest slot, and the nearest slot silently swallowed the subject, the body
   * and the attachment list on both package sends for the life of #1136. A
   * fragment is always truthy, so `content ?? (…)` never fell through and
   * nothing typed, linted or tested could see it — which is why the prop is no
   * longer called `content`: the name was the invitation.</p>
   */
  notices?: ReactNode;

  /** Replaces the subject/body/attachment block entirely — for a draft still
   *  loading, or a read that failed.
   *
   *  <p>🔴 <b>REPLACES. It is not an append slot.</b> Anything that should
   *  appear BESIDE the message goes in `notices`. Pinned by
   *  `SubmissionPackagePanel.test.tsx`, which fails if either package modal
   *  passes this on its loaded path.</p>
   *
   *  <p>🔴 <b>The NAME is part of the fix.</b> This was `content` — a word that
   *  describes every slot on the component and warns about none of them, so the
   *  caller who wanted "put this in the modal" reached for it and silently lost
   *  the message block. A prop whose name states what it does to its neighbours
   *  cannot be picked up by mistake.</p> */
  replacesPreviewBody?: ReactNode;

  /** Trailing sentence: what sending does and does not do. */
  footnote?: ReactNode;

  /** Footer, far left. The package's "Download PDF". */
  secondaryAction?: ReactNode;
  /** Footer, beside the confirm. The package's "I sent it myself". */
  alternateAction?: ReactNode;

  confirmLabel: string;
  onConfirm: () => void;
  confirmDisabled?: boolean;
  /**
   * Why the confirm is disabled, rendered at the foot of the body beside the
   * button it explains.
   *
   * <p>🔴 Text, never a tooltip. A disabled button with a hover-only reason is
   * a dead end for anyone who does not hover, and the reason is the only thing
   * that tells a specialist what to change.</p>
   */
  confirmBlockedReason?: ReactNode;
  confirmTestId?: string;
  pending?: boolean;
}

export function SendPreviewModal({
  title,
  onClose,
  toName,
  toEmail,
  audienceNote,
  identity,
  repliesReachPhoenix = false,
  subject,
  onSubjectChange,
  body,
  onBodyChange,
  bodyRows = 12,
  renderPreview,
  attachments,
  selectableAttachments,
  carriedOverNote,
  signatureBlock,
  roleStatement,
  messageActions,
  notices,
  replacesPreviewBody,
  footnote,
  secondaryAction,
  alternateAction,
  confirmLabel,
  onConfirm,
  confirmDisabled = false,
  confirmBlockedReason,
  confirmTestId,
  pending = false,
}: SendPreviewModalProps) {
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);

  // What the rendered preview currently reflects. Compared against the live
  // fields so the pane can say "out of date" rather than quietly showing stale
  // chrome around edited words — a preview that looks current and is not is the
  // failure this whole pane exists to end.
  const [previewedFor, setPreviewedFor] = useState<string | null>(null);

  const current = `${subject ?? ""}\u0000${body ?? ""}`;
  const stale = previewHtml !== null && previewedFor !== current;

  // Latest-wins. Two refreshes in flight can otherwise land out of order and
  // leave the pane showing the earlier one while claiming to be current.
  const requestSeq = useRef(0);

  const refreshPreview = useCallback(async () => {
    if (!renderPreview) return;

    const seq = ++requestSeq.current;
    const forValue = `${subject ?? ""}\u0000${body ?? ""}`;

    setPreviewBusy(true);
    setPreviewError(null);
    try {
      const html = await renderPreview({ subject: subject ?? "", body: body ?? "" });
      if (seq !== requestSeq.current) return;
      setPreviewHtml(html);
      setPreviewedFor(forValue);
    } catch {
      if (seq !== requestSeq.current) return;
      setPreviewError("The preview could not be rendered. Your message is unaffected.");
    } finally {
      if (seq === requestSeq.current) setPreviewBusy(false);
    }
  }, [renderPreview, subject, body]);

  // Once on open. Deliberately NOT on every keystroke: a request per character is
  // wrong regardless of how cheap the render is, and the refresh control makes
  // the cost visible instead of hiding it.
  const loadedOnce = useRef(false);
  useEffect(() => {
    if (!renderPreview || loadedOnce.current) return;
    loadedOnce.current = true;
    void refreshPreview();
  }, [renderPreview, refreshPreview]);

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      size="lg"
      footer={
        <div className="flex flex-wrap items-center justify-between gap-space-3">
          <div>{secondaryAction}</div>
          <div className="flex flex-wrap items-center gap-space-3">
            <Button variant="secondary" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
            {alternateAction}
            <Button
              onClick={onConfirm}
              disabled={pending || confirmDisabled}
              data-testid={confirmTestId}
            >
              {pending ? "Sending…" : confirmLabel}
            </Button>
          </div>
        </div>
      }
    >
      <div className="grid gap-space-4" data-testid="send-preview">
        <Recipient name={toName} email={toEmail} note={audienceNote} />
        <Identity identity={identity} repliesReachPhoenix={repliesReachPhoenix} />

        {notices ? <div className="grid gap-space-2">{notices}</div> : null}

        {replacesPreviewBody ?? (
          <>
            {subject !== undefined ? (
              <Field label="Subject">
                {onSubjectChange ? (
                  <Textarea
                    rows={1}
                    value={subject ?? ""}
                    aria-label="Subject"
                    data-testid="send-preview-subject-input"
                    onChange={(e) => onSubjectChange(e.target.value)}
                  />
                ) : (
                  <p className="text-small text-onyx m-0" data-testid="send-preview-subject">
                    {subject}
                  </p>
                )}
              </Field>
            ) : null}

            {body !== undefined ? (
              <Field label="Message">
                {onBodyChange ? (
                  <Textarea
                    rows={bodyRows}
                    value={body ?? ""}
                    aria-label="Message"
                    data-testid="send-preview-body-input"
                    onChange={(e) => onBodyChange(e.target.value)}
                  />
                ) : (
                  <div className="max-h-72 overflow-y-auto rounded-md border border-border-light bg-white-warm px-space-3 py-space-2">
                    <p
                      className="text-small text-onyx whitespace-pre-wrap m-0"
                      data-testid="send-preview-body"
                    >
                      {body}
                    </p>
                  </div>
                )}
              </Field>
            ) : null}

            {renderPreview ? (
              <div
                className="rounded-md border border-border-light"
                data-testid="send-preview-rendered"
              >
                <div className="flex flex-wrap items-center justify-between gap-space-2 border-b border-border-light bg-white-warm px-space-3 py-space-2">
                  <div className="flex items-center gap-space-2">
                    <span className="text-caption font-semibold text-onyx">
                      What the recipient sees
                    </span>
                    {stale ? (
                      <span
                        className="text-caption text-status-warning"
                        data-testid="send-preview-rendered-stale"
                      >
                        Out of date — refresh to see your edits
                      </span>
                    ) : null}
                  </div>
                  <Button
                    variant="text"
                    onClick={() => void refreshPreview()}
                    disabled={previewBusy}
                    data-testid="send-preview-rendered-refresh"
                  >
                    {previewBusy ? "Rendering…" : "Refresh preview"}
                  </Button>
                </div>

                {previewError ? (
                  <p
                    className="text-caption text-status-error m-0 px-space-3 py-space-3"
                    role="alert"
                    data-testid="send-preview-rendered-error"
                  >
                    {previewError}
                  </p>
                ) : previewHtml ? (
                  /*
                   * Sandboxed and srcDoc'd, following the admin template editor.
                   * This is a full HTML document from the server carrying the
                   * transport disclaimer, and it may contain an admin-authored
                   * inline tag from the email allowlist — it must never be
                   * injected into the SPA's own document.
                   */
                  <iframe
                    sandbox=""
                    srcDoc={previewHtml}
                    title="Rendered email preview"
                    className="block h-96 w-full border-0 bg-white-pure"
                    data-testid="send-preview-rendered-frame"
                  />
                ) : (
                  <p className="text-caption text-gray-mid m-0 px-space-3 py-space-3">
                    Rendering the preview…
                  </p>
                )}
              </div>
            ) : null}

            {messageActions ? (
              <div
                className="flex flex-wrap items-center gap-space-3"
                data-testid="send-preview-message-actions"
              >
                {messageActions}
              </div>
            ) : null}

            <LockedTrailer
              caption="Your sign-off, added automatically"
              text={signatureBlock}
              testId="send-preview-signature"
            />

            <RoleStatement text={roleStatement} />

            {selectableAttachments ? (
              <AttachmentPicker
                rows={selectableAttachments}
                carriedOverNote={carriedOverNote}
              />
            ) : (
              <Attachments items={attachments} />
            )}
          </>
        )}

        {confirmBlockedReason ? (
          <p className="text-small text-status-error m-0" data-testid="send-preview-blocked">
            {confirmBlockedReason}
          </p>
        ) : null}

        {footnote ? <p className="text-caption text-gray-mid m-0">{footnote}</p> : null}
      </div>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="grid gap-space-2">
      <span className="text-caption text-gray-mid uppercase tracking-wider">{label}</span>
      {children}
    </label>
  );
}

function Recipient({
  name,
  email,
  note,
}: {
  name?: string | null;
  email?: string | null;
  note?: ReactNode;
}) {
  return (
    <p className="text-small text-onyx m-0" data-testid="send-preview-to">
      Phoenix will send this to{" "}
      {name ? <strong>{name}</strong> : null}
      {name && email ? " at " : null}
      {email ? <strong>{email}</strong> : null}
      {!name && !email ? <strong>the address on file</strong> : null}
      {note ? <>, {note}</> : "."}
    </p>
  );
}

/**
 * Who it comes from, and where a reply goes.
 *
 * <p>Neither of the two confirmations this replaced showed either, so a
 * specialist could not tell whether a broker hitting Reply would reach them or a
 * do-not-reply mailbox — and until very recently the honest answer was the
 * second one for every message the platform sent.</p>
 *
 * <p>Renders nothing while the identity is unknown. An absent block is honest; a
 * guessed address beside a Send button is the kind of detail somebody relies
 * on.</p>
 */
function Identity({
  identity,
  repliesReachPhoenix,
}: {
  identity?: SenderIdentity;
  repliesReachPhoenix: boolean;
}) {
  if (!identity && !repliesReachPhoenix) return null;

  const replyTo = repliesReachPhoenix ? null : identity?.replyToEmail;

  return (
    <div
      className="grid gap-space-1 rounded-md border border-border-light bg-white-warm px-space-3 py-space-2"
      data-testid="send-preview-identity"
    >
      <p className="text-caption text-gray-mid m-0">
        {/* Described rather than quoted: the From display name is set on the ACS
            domain, not by this application, so there is no string here worth
            trusting. */}
        Sent by <strong className="text-onyx">Phoenix Capital</strong>, under your name.
      </p>
      {replyTo ? (
        <p className="text-caption text-gray-mid m-0">
          Replies reach you at{" "}
          <strong className="text-onyx" data-testid="send-preview-reply-to">
            {replyTo}
          </strong>
          .
        </p>
      ) : (
        <p className="text-caption text-gray-mid m-0" data-testid="send-preview-no-reply-to">
          {repliesReachPhoenix
            ? "Replies reach Phoenix Capital rather than you directly."
            : "You have no reply address on file, so replies reach Phoenix Capital. "
              + "Add one on your profile to receive them yourself."}
        </p>
      )}
    </div>
  );
}

/**
 * The trailer the transport appends to every send.
 *
 * <p>🔴 <b>A `div` and a `span`, deliberately NOT the `Field` helper beside
 * it.</b> `Field` is a `<label>`, and a label wrapping static prose labels
 * nothing — it announces to a screen reader as a control that is not there.
 * There is no control here, and that is the entire content of the block.</p>
 *
 * <p>The label states the rule rather than naming the block, because "Role
 * statement" answers a question nobody asked and leaves the one they will ask —
 * "why can I not change this?" — unanswered.</p>
 */
function RoleStatement({ text }: { text?: string | null }) {
  return (
    <LockedTrailer
      caption="Phoenix always adds this, and it cannot be edited"
      text={text}
      testId="send-preview-role-statement"
    />
  );
}

/**
 * Text the transport appends, shown beneath the message it is appended to.
 *
 * <p>Absent text renders NOTHING rather than a placeholder: "no signature on
 * file" belongs on the identity block, which is about the sender, not in the
 * run of the message, which is about what the recipient reads.</p>
 */
function LockedTrailer({
  caption,
  text,
  testId,
}: {
  caption: string;
  text?: string | null;
  testId: string;
}) {
  if (!text) return null;

  return (
    <div className="grid gap-space-2" data-testid={testId}>
      <span className="text-caption text-gray-mid uppercase tracking-wider">
        {caption}
      </span>
      <div className="rounded-md border border-border-light bg-white-warm px-space-3 py-space-2">
        <p
          className="text-small text-gray-mid whitespace-pre-wrap m-0"
          data-testid={`${testId}-text`}
        >
          {text}
        </p>
      </div>
    </div>
  );
}

/**
 * The per-send picker: what is going out, and the choice of what does.
 *
 * <p>🔴 <b>`chromeThreshold={Infinity}`, and it is not a style preference.</b>
 * `SubTable` earns search, sort and a pager above ten rows — all three of which
 * are wrong over a picker. A row paged out of view is a row that still counts
 * toward the running total below, so the reader would be looking at a figure
 * for documents they cannot see, and a sort would reorder a set whose order is
 * the package's own. Every row a send can attach must be on screen at once.</p>
 *
 * <p>🔴 <b>The tick is the control; the ROW is not.</b> `SubTable` offers
 * `onRowClick` and it is deliberately unused: a row click over a checkbox
 * either double-fires or swallows the click, and neither failure is visible in
 * the markup.</p>
 */
function AttachmentPicker({
  rows,
  carriedOverNote,
}: {
  rows: readonly SendPreviewSelectableAttachment[];
  carriedOverNote?: ReactNode;
}) {
  if (rows.length === 0) return null;

  const anyDownload = rows.some((r) => r.onDownload);
  const selected = rows.filter((r) => r.selected);
  const totalBytes = selected.reduce((sum, r) => sum + (r.sizeBytes ?? 0), 0);

  const columns: SubTableColumn<SendPreviewSelectableAttachment>[] = [
    {
      key: "attach",
      header: "Attach",
      className: "w-16",
      render: (row) => (
        <input
          type="checkbox"
          className="h-4 w-4 accent-gold disabled:cursor-not-allowed disabled:opacity-40"
          checked={row.selected}
          disabled={row.disabled === true || !row.onToggle}
          onChange={() => row.onToggle?.()}
          aria-label={`Attach ${row.name}`}
          data-testid={`send-preview-attach-${row.key}`}
        />
      ),
    },
    {
      key: "document",
      header: "Document",
      render: (row) => (
        <div className="grid gap-space-1">
          <span className="text-small text-onyx">{row.name}</span>
          {row.carriedOver && carriedOverNote ? (
            <span
              className="text-caption text-status-warning"
              data-testid={`send-preview-carried-over-${row.key}`}
            >
              {carriedOverNote}
            </span>
          ) : null}
          {row.disabledReason ? (
            <span
              className="text-caption text-gray-mid"
              data-testid={`send-preview-attach-blocked-${row.key}`}
            >
              {row.disabledReason}
            </span>
          ) : null}
        </div>
      ),
      searchText: (row) => row.name,
    },
    {
      key: "size",
      header: "Size",
      align: "right",
      // An em dash, not "0 KB": a document nobody has written has no size, and
      // a zero is a measurement.
      render: (row) =>
        typeof row.sizeBytes === "number" && row.sizeBytes > 0 ? formatSize(row.sizeBytes) : "—",
    },
  ];

  if (anyDownload) {
    columns.push({
      key: "download",
      header: "",
      align: "right",
      render: (row) =>
        row.onDownload ? (
          <Button variant="text" size="sm" onClick={row.onDownload}>
            Download
          </Button>
        ) : null,
    });
  }

  return (
    <div className="grid gap-space-2">
      <span className="text-caption text-gray-mid uppercase tracking-wider">Attachments</span>
      <SubTable
        columns={columns}
        rows={[...rows]}
        rowKey={(row) => row.key}
        caption="Documents this send will attach"
        chromeThreshold={Number.POSITIVE_INFINITY}
        rowTestId={(row) => `send-preview-attachment-row-${row.key}`}
        data-testid="send-preview-attachment-picker"
      />
      {/* Live, and derived from the ticks rather than from the manifest — the
          two agree at rest and this is the one that keeps agreeing. */}
      <p className="text-caption text-gray-mid m-0" data-testid="send-preview-attachment-total">
        {selected.length} of {rows.length} selected
        {totalBytes > 0 ? ` · ${formatSize(totalBytes)}` : ""}
      </p>
    </div>
  );
}

/**
 * What travels with the message, by name.
 *
 * <p>The package send attaches a PDF and said so only in a sentence of prose
 * ("The package PDF is attached automatically"), which is a promise rather than
 * a statement of fact — it named neither the file nor its size, so a specialist
 * could not tell a freshly regenerated package from a stale one, or notice a
 * document that had come out empty.</p>
 */
function Attachments({ items }: { items?: readonly SendPreviewAttachment[] }) {
  if (!items || items.length === 0) return null;

  return (
    <div className="grid gap-space-2">
      <span className="text-caption text-gray-mid uppercase tracking-wider">
        {items.length === 1 ? "Attachment" : "Attachments"}
      </span>
      <ul className="m-0 grid gap-space-1 p-0 list-none" data-testid="send-preview-attachments">
        {items.map((a) => (
          <li key={a.name} className="text-small text-onyx flex items-center gap-space-2">
            <span>
              {a.name}
              {typeof a.sizeBytes === "number" && a.sizeBytes > 0 ? (
                <span className="text-gray-mid"> · {formatSize(a.sizeBytes)}</span>
              ) : null}
            </span>
            {a.onDownload ? (
              <Button
                variant="text"
                size="sm"
                onClick={a.onDownload}
                data-testid={`send-preview-download-${a.name}`}
              >
                Download
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Whole KB below a megabyte, one decimal above. Enough to tell a real document
 *  from an empty one, which is all this needs to do. */
function formatSize(bytes: number): string {
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.max(1, Math.round(kb))} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}
